/**
 * Enhanced SearchPalette Component
 *
 * A configurable search palette that supports different model types and routing configurations.
 *
 * @example Basic usage (all search types enabled with default routes):
 * ```hbs
 * <SearchPalette />
 * ```
 *
 * @example People-only search with company filter:
 * ```hbs
 * <SearchPalette
 *   @searchTypes={{hash enablePeople=true enableTas=false enablePslf=false}}
 *   @filters={{hash companyId="12345"}}
 * />
 * ```
 *
 * @example Custom routing configuration:
 * ```hbs
 * <SearchPalette
 *   @routeConfig={{hash
 *     peopleRoute="app.users.show"
 *     tasRoute="app.assistance.show"
 *     pslfRoute="app.pslf.detail"
 *   }}
 * />
 * ```
 *
 * @example Combined configuration:
 * ```hbs
 * <SearchPalette
 *   @searchTypes={{hash enablePeople=true enableTas=true enablePslf=false}}
 *   @routeConfig={{hash peopleRoute="custom.people.show"}}
 *   @filters={{hash companyId="67890" additionalFilters=(hash department="HR")}}
 * />
 * ```
 */

import { and, not } from 'tio-ui/utilities';
import { Button } from 'tio-ui/components/buttons';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { OpenSearchQueryBuilder, highlight } from '../../utils/open-search-builder.ts';
import { OpenSearchResponseBuilder } from '../../utils/open-search-response-builder.ts';
import { type FilterClause } from '../../utils/open-search-builder.ts';
import { Overlay } from 'tio-ui/components/overlays';
import { PhMagnifyingGlass, PhGlobeHemisphereWest, PhSmileySad } from 'ember-phosphor-icons';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { Person, TasProgramInstance, PslfForm } from '../../models/search.ts';
import type { TOC } from '@ember/component/template-only';
import type StoreService from '@ember-data/store';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';

/**
 * List of field names that are considered ID-like for search matching.
 */
const ID_FIELDS = ['tas_program_instance_id_keyword', 'tas_application_id_keyword'] as const;

/**
 * Model-specific search fields for Person index.
 * Uses new Person index field names (no .analyzed suffix).
 */
const PERSON_SEARCH_FIELDS = ['first_name', 'last_name', 'all_emails', 'all_employee_names'];

/**
 * Returns true if the string is a 19-digit number (ID-like).
 */
const isIdLike = (s: string) => /^\d{19}$/.test(s);

/**
 * Supported model types for search results.
 */
type Models = Person | TasProgramInstance | PslfForm;

/**
 * A single search result item for a given model type.
 * The OpenSearchResponseBuilder always adds an id field, even if the original type doesn't have one.
 */
type ResultItem<M extends Models> = M & {
  id: string;
  _index?: string;
  highlight?: Record<string, string[]>;
};

/**
 * An array of search result items for a given model type.
 */
type ResultItems<M extends Models> = Array<ResultItem<M>>;

export interface SearchResultSectionSignature<M extends Models = Models> {
  Args: {
    title: string;
    items: ResultItems<M>;
    selectItem: (item: ResultItem<M>) => void;
  };
  Blocks: {
    default: [item: ResultItem<M>];
  };
}

const SearchResultSection: TOC<SearchResultSectionSignature> = <template>
  <div
    class="sticky top-0 z-10 px-4 py-2.5 text-xs font-semibold bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white"
    data-test-id="{{@title}}-section"
  >
    {{@title}}
  </div>

  <ul class="mt-2 text-sm text-gray-800">
    {{#each @items as |item|}}
      <li>
        <button
          type="button"
          class="w-full text-left block rounded-md px-4 py-2 transition-colors cursor-pointer select-none focus:outline-none text-gray-800 hover:bg-gray-50 dark:hover:bg-white/10 aria-selected:bg-indigo-600 aria-selected:text-white dark:aria-selected:bg-indigo-500 aria-selected:hover:bg-indigo-600 dark:aria-selected:hover:bg-indigo-500"
          {{on "click" (fn @selectItem item)}}
          data-test-id="{{@title}}-item"
        >
          {{yield item}}
        </button>
      </li>
    {{/each}}
  </ul>
</template>;

/**
 * Configuration for search result routing
 */
interface SearchRouteConfig {
  /** Base route for people results. Used when navigating to a selected person. Defaults to 'authenticated.people.show' */
  peopleRoute?: string;
  /** Base route for TAS program instances. Defaults to 'authenticated.tuition-assistance.program-instances.show' */
  tasRoute?: string;
  /** Base route for TAS applications. Defaults to 'authenticated.tuition-assistance.applications.show' */
  tasApplicationRoute?: string;
  /** Base route for PSLF forms. Defaults to 'authenticated.pslf.forms.show' */
  pslfRoute?: string;
}

/**
 * Required route configuration with all routes defined
 */
interface RequiredSearchRouteConfig {
  peopleRoute: string;
  tasRoute: string;
  tasApplicationRoute: string;
  pslfRoute: string;
}

/**
 * Configuration for which search types are enabled
 */
interface SearchTypeConfig {
  /** Whether to enable people search. Defaults to true */
  enablePeople?: boolean;
  /** Whether to enable TAS program search. Defaults to true */
  enableTas?: boolean;
  /** Whether to enable PSLF form search. Defaults to true */
  enablePslf?: boolean;
}

/**
 * Additional query parameters to include in searches
 */
interface SearchFilters {
  /** Company ID to filter results by */
  companyId?: string;
  /** Additional filters to apply to all searches */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalFilters?: Record<string, any>;
}

interface SearchPaletteSignature {
  Element: HTMLButtonElement;
  Args: {
    /** Configuration for result routing */
    routeConfig?: SearchRouteConfig;
    /** Configuration for which search types are enabled */
    searchTypes?: SearchTypeConfig;
    /** Additional filters to apply to searches */
    filters?: SearchFilters;
  };
}

export default class SearchPalette extends Component<SearchPaletteSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked isOpen = false;
  @tracked people: ResultItem<Person>[] = [];
  @tracked tasPrograms: ResultItem<TasProgramInstance>[] = [];
  @tracked pslfForms: ResultItem<PslfForm>[] = [];
  @tracked searchTerm = '';

  /** Default route configuration */
  private get defaultRouteConfig(): RequiredSearchRouteConfig {
    return {
      peopleRoute: 'authenticated.people.show',
      tasRoute: 'authenticated.tuition-assistance.program-instances.show',
      tasApplicationRoute: 'authenticated.tuition-assistance.applications.show',
      pslfRoute: 'authenticated.pslf.forms.show',
    };
  }

  private get defaultSearchTypes(): SearchTypeConfig {
    return {
      enablePeople: true,
      enableTas: true,
      enablePslf: true,
    };
  }

  private get routeConfig(): RequiredSearchRouteConfig {
    return { ...this.defaultRouteConfig, ...this.args.routeConfig };
  }

  private get searchTypes(): SearchTypeConfig {
    return { ...this.defaultSearchTypes, ...this.args.searchTypes };
  }

  private get filters(): SearchFilters {
    return this.args.filters ?? {};
  }

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ApplicationState.UPDATED_ON');
  };

  private buildQuery(modelType: string, tokens: string[]) {
    const idTokens = tokens.filter(isIdLike);
    const textTokens = tokens.filter((t) => !isIdLike(t));

    const b = new OpenSearchQueryBuilder().setPagination(1, 5);

    // Add company filter if provided
    if (this.filters.companyId) {
      b.addFilter({ term: { company_id: this.filters.companyId } });
    }

    // Add any additional filters
    if (this.filters.additionalFilters) {
      Object.entries(this.filters.additionalFilters).forEach(([key, value]) => {
        b.addFilter({ term: { [key]: value } });
      });
    }

    if (textTokens.length) {
      // Use model-specific fields for Person to work with OLD production index
      const searchFields = modelType === 'Person' ? PERSON_SEARCH_FIELDS : ['*'];
      const mmClauses: FilterClause[] = textTokens.map((t) => ({
        multi_match: {
          query: t,
          fields: searchFields,
          operator: 'or',
          type: 'best_fields',
          fuzziness: 'AUTO' as const,
          lenient: true, // Skip fields that can't be searched (e.g., unindexed fields)
        },
      }));
      b.addShould({ bool: { should: mmClauses, minimum_should_match: 1 } });
    }

    for (const tok of idTokens) {
      const perTokenShould: FilterClause[] = [];
      for (const f of ID_FIELDS) {
        // keep as string to avoid 19-digit precision loss in JS Numbers
        perTokenShould.push({ term: { [f]: tok } });
        perTokenShould.push({ term: { [`${f}.keyword`]: tok } });
      }
      b.addShould({ bool: { should: perTokenShould, minimum_should_match: 1 } });
    }

    // Require at least one of the should groups (text or id) to match
    if (textTokens.length || idTokens.length) {
      b.setMinimumShouldMatch(1);
    }

    return { 'model-type': modelType, 'search-body': b.build() };
  }

  private async runSearch<T extends Models>(modelType: string, tokens: string[]) {
    const query = this.buildQuery(modelType, tokens);
    const response = await this.store.adapterFor('search').search(query);
    const responseBuilder = OpenSearchResponseBuilder.from<T>(response);
    return responseBuilder.toPaginatedResult({ includeHighlights: true });
  }

  peopleTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.people = [];
      return;
    }
    const data = await this.runSearch<Person>('Person', tokens);
    this.people = data.items ?? [];
  });

  tasTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.tasPrograms = [];
      return;
    }
    const data = await this.runSearch<TasProgramInstance>('TasProgramInstance', tokens);
    this.tasPrograms = data.items ?? [];
  });

  pslfTask = restartableTask(async (tokens: string[]) => {
    if (tokens.length === 0) {
      this.pslfForms = [];
      return;
    }
    const data = await this.runSearch<PslfForm>('PslfForm', tokens);
    this.pslfForms = data.items as ResultItem<PslfForm>[];
  });

  debouncedSearchTask = restartableTask(async (raw: string) => {
    this.searchTerm = raw; // reflect UI immediately
    const tokens = raw.trim().split(/\s+/).filter(Boolean);

    await timeout(250); // debounce

    // Only run searches for enabled types
    if (this.searchTypes.enablePeople) {
      this.peopleTask.perform(tokens);
    } else {
      this.people = [];
    }

    if (this.searchTypes.enableTas) {
      this.tasTask.perform(tokens);
    } else {
      this.tasPrograms = [];
    }

    if (this.searchTypes.enablePslf) {
      this.pslfTask.perform(tokens);
    } else {
      this.pslfForms = [];
    }
  });

  search = (e: Event) => {
    this.debouncedSearchTask.perform((e.target as HTMLInputElement).value ?? '');
  };

  get hasResults() {
    return this.people.length + this.tasPrograms.length + this.pslfForms.length > 0;
  }

  get searchDescription() {
    const enabledTypes = [];
    if (this.searchTypes.enablePeople) enabledTypes.push('People');
    if (this.searchTypes.enableTas) enabledTypes.push('Assistances');
    if (this.searchTypes.enablePslf) enabledTypes.push('PSLF');

    if (enabledTypes.length === 0) return 'Search';
    if (enabledTypes.length === 1) return `Search for ${enabledTypes[0]}`;
    if (enabledTypes.length === 2) return `Search for ${enabledTypes.join(' and ')}`;

    const lastType = enabledTypes.pop();
    return `Search for ${enabledTypes.join(', ')}, and ${lastType}`;
  }

  get searchSubDescription() {
    const enabledTypes = [];
    if (this.searchTypes.enablePeople) enabledTypes.push('people');
    if (this.searchTypes.enableTas) enabledTypes.push('assistances');
    if (this.searchTypes.enablePslf) enabledTypes.push('PSLF');

    if (enabledTypes.length === 0) return 'No search types enabled.';
    if (enabledTypes.length === 1) return `Quickly access ${enabledTypes[0]} by running a search.`;
    if (enabledTypes.length === 2)
      return `Quickly access ${enabledTypes.join(' and ')} by running a global search.`;

    const lastType = enabledTypes.pop();
    return `Quickly access ${enabledTypes.join(', ')}, and ${lastType} by running a global search.`;
  }

  selectItem = (item: ResultItem<Models>) => {
    let route: string;
    let modelId: string;

    // Use the _index property to determine model type based on prefix
    const indexName = item._index;
    if (indexName?.startsWith('people')) {
      route = this.routeConfig.peopleRoute;
      modelId = item.id!;
    } else if (indexName?.startsWith('tas_program_instances')) {
      const tasItem = item as ResultItem<TasProgramInstance>;
      const applicationId = tasItem.tasApplicationIdKeyword;
      if (applicationId) {
        route = this.routeConfig.tasApplicationRoute;
        modelId = String(applicationId);
      } else {
        route = this.routeConfig.tasRoute;
        modelId = tasItem.id!;
      }
    } else if (indexName?.startsWith('pslf_forms')) {
      route = this.routeConfig.pslfRoute;
      modelId = item.id!;
    } else {
      throw new Error(`Unknown model type for item with index: ${indexName}`);
    }

    this.isOpen = false;
    this.searchTerm = '';
    this.people = [];
    this.tasPrograms = [];
    this.pslfForms = [];
    this.router.transitionTo(route, modelId);
  };

  <template>
    <Button
      @intent="primary"
      @appearance="outlined"
      {{on "click" (fn (mut this.isOpen) true)}}
      data-test-search-palette-button
      ...attributes
    >
      <PhMagnifyingGlass />
    </Button>
    <Overlay @isOpen={{this.isOpen}} @onClose={{fn (mut this.isOpen) false}} class="z-[90]">
      <div
        class="mx-auto w-full max-w-xl my-24 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-inset ring-black/10 dark:ring-white/10 flex flex-col min-h-[22rem] max-h-[80vh]"
      >
        {{! Header }}
        <div class="relative p-3 border-b border-gray-200 border-black/5 dark:border-white/10">
          <PhMagnifyingGlass
            class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400"
          />
          <input
            {{on "input" this.search}}
            type="text"
            placeholder="Search..."
            class="h-12 w-full bg-transparent pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm dark:text-white"
          />
        </div>

        {{! Body }}
        {{#if this.hasResults}}
          <div class="flex-1 overflow-y-auto">
            {{#if (and this.searchTypes.enablePeople this.people.length)}}
              <SearchResultSection
                @title="People"
                @selectItem={{this.selectItem}}
                @items={{this.people}}
                as |item|
              >
                {{htmlSafe (highlight item "firstName")}}
                {{htmlSafe (highlight item "lastName")}}
                {{! @glint-expect-error: add this to the type}}
                {{#each item.employees as |employee|}}
                  <p
                    class="text-sm text-gray-500 group-aria-selected:text-gray-700 dark:group-aria-selected:text-gray-300"
                  >
                    {{employee.companyName}}
                  </p>
                {{/each}}
              </SearchResultSection>
            {{/if}}
            {{#if (and this.searchTypes.enableTas this.tasPrograms.length)}}
              <SearchResultSection
                @title="Assistances"
                @items={{this.tasPrograms}}
                @selectItem={{this.selectItem}}
                as |assistance|
              >
                {{htmlSafe (highlight assistance "employeeName")}}
                <p
                  class="text-sm text-gray-500 group-aria-selected:text-gray-700 dark:group-aria-selected:text-gray-300"
                >{{htmlSafe (highlight assistance "id")}}
                  &bull;
                  {{htmlSafe (highlight assistance "companyName")}}
                  &bull;
                  {{! @glint-expect-error: types are being weird here}}
                  {{#if assistance.applicationState}}
                    {{! @glint-expect-error: types are being weird here}}
                    {{this.statusText assistance.applicationState}}
                  {{else}}
                    {{! @glint-expect-error: types are being weird here}}
                    {{this.statusText assistance.programInstanceState}}
                  {{/if}}
                </p>
              </SearchResultSection>
            {{/if}}
            {{#if (and this.searchTypes.enablePslf this.pslfForms.length)}}
              <SearchResultSection
                @title="PSLF Forms"
                @items={{this.pslfForms}}
                @selectItem={{this.selectItem}}
                as |form|
              >
                {{htmlSafe (highlight form "borrowerName")}}
                <p
                  class="text-sm text-gray-500 group-aria-selected:text-gray-700 dark:group-aria-selected:text-gray-300"
                >
                  {{htmlSafe (highlight form "id")}}
                  &bull;
                  {{htmlSafe (highlight form "formStatus")}}
                </p>
              </SearchResultSection>
            {{/if}}
          </div>
        {{/if}}
        {{#unless this.searchTerm}}
          <div class="flex-1 grid place-items-center p-10 text-center">
            <div>
              <PhGlobeHemisphereWest class="mx-auto size-6 text-gray-400" />
              <p class="mt-4 font-semibold text-gray-900 dark:text-white">
                {{this.searchDescription}}
              </p>
              <p class="mt-2 text-gray-500">
                {{this.searchSubDescription}}
              </p>
            </div>
          </div>
        {{/unless}}

        {{#if (and this.searchTerm (not this.hasResults))}}
          {{! No Results Found }}
          <div
            class="border-t border-gray-200 border-black/5 dark:border-white/10 p-6 text-center text-sm"
          >
            <PhSmileySad class="mx-auto size-6 text-gray-400" />
            <p class="mt-4 font-semibold text-gray-900 dark:text-white">No results found</p>
            <p class="mt-2 text-gray-500">
              We couldn’t find anything with that term. Please try again.
            </p>
          </div>
        {{/if}}
      </div>
    </Overlay>
  </template>
}
